/**
 * `resolveGatewayDisplay` — the SINGLE mapping from (WS connection state ×
 * runtime lifecycle × repair flag) to the glyph, colour and copy that
 * represent the workspace connection.
 *
 * Extracted from `SidebarUtility` 2026-07-30 when the operator moved the
 * connector into the chat infobar: two surfaces rendering the same status
 * from two copies of this logic is exactly how they drift into disagreeing
 * about whether the workspace is up. One implementation, N importers —
 * the same rule `isRightSidebarOnScreen` and `computeCaseBriefDigest` follow.
 */
import { AlertTriangle, Loader2, Pause, Power, Unplug, Wifi, WifiOff, Wrench } from "lucide-react";

import type { RuntimeState } from "@/stores/useGatewayStore";

const GATEWAY_KEY_BY_STATE: Record<string, string> = {
  disconnected: "header.gateway.disconnected",
  connecting: "header.gateway.connecting",
  authenticating: "header.gateway.authenticating",
  connected: "header.gateway.connected",
  reconnecting: "header.gateway.reconnecting",
  error: "header.gateway.error",
  unauthorized: "header.gateway.unauthorized",
};


/**
 * Feature C — the connection icon is the at-a-glance cue: BOTH glyph and colour
 * change with the runtime state. Merge rule: while the WS socket is connected,
 * trust it → green "Connected"; only when disconnected/reconnecting do we colour
 * by `runtimeState`. Colours mirror the LexPortal RuntimeStateBadge.
 */
export interface GatewayDisplay {
  Icon: typeof Wifi;
  /** Tailwind classes for the segment's FILL + type colour (not a text tint). */
  colour: string;
  spin?: boolean;
  labelKey: string;
  msgKey: string;
}

/**
 * COLOUR LIVES IN THE BACKGROUND, NOT THE TEXT — operator ruling 2026-07-31,
 * following VSCode, whose remote/status blocks are solid colour fills with
 * white type rather than tinted text on the bar's own ground.
 *
 * Why it is the better call here: the bar's messages are deliberately
 * colourless, so a tinted WORD beside them reads as just another message that
 * happens to be green. A filled BLOCK reads as a different kind of thing — a
 * standing state you scan for rather than a sentence you read — which is
 * exactly what the connector is. It also survives at a glance across a room,
 * where a coloured 12px word does not.
 *
 * Text stays white and thin in every state: the FILL carries the meaning, so
 * the type does not have to, and a bold white on a saturated ground would be
 * the loudest thing in the workspace.
 *
 * FULLY OPAQUE, one step lighter (operator 2026-07-31). The earlier /90 alpha
 * let the bar's dark coffee ground bleed through and muddied the hue — a fill
 * meant to be read at a glance should not be a blend of two colours that
 * changes with whatever sits behind it.
 */
/**
 * ⚠ CONTRAST AGAINST WHITE, MEASURED (canvas-resolved sRGB, not eyeballed —
 * these tokens are oklch, so hand-parsing them gives fiction):
 *   emerald-600 3.65 · amber-500 2.13 · amber-600 3.20 · amber-700 5.03
 *   red-600 4.77 · slate-500 3.96 · slate-600 7.63 · gold-700 5.32
 * WCAG AA for normal text is 4.5:1.
 *
 * EVERY STATE IS ALPHA'D AT /50 (operator 2026-07-31) — one visual language
 * for the block, so it never changes weight as well as colour when the
 * connection changes. Composited over the strip's ground rgb(35,32,28),
 * MEASURED live:
 *
 *   state          effective        white label     vs the bar
 *   connected      rgb(18,93,65)        7.87           2.06
 *   busy           rgb(111,55,14)       9.42           1.72
 *   down           rgb(133,16,20)      10.09           1.61
 *   paused         rgb(63,57,52)       11.37           1.43
 *
 * Read those honestly, because they change what carries the meaning.
 *
 * THE LABEL IS NOW THE SIGNAL, NOT THE FILL. Every state clears AA for its
 * white text by a wide margin (7.9–11.4), while the fills sit at 1.4–2.1
 * against the bar — barely a tint in LUMINANCE terms. Worse, at /50 the
 * fills are hard to tell APART: busy vs down is 1.07, red vs paused 1.13.
 * So a lawyer glancing at colour alone can no longer reliably distinguish
 * "reconnecting" from "your workspace is gone".
 *
 * That is acceptable ONLY because colour was never the sole channel here:
 * the GLYPH differs per state (Wifi / Wrench / Loader2 / AlertTriangle /
 * Power / Unplug / Pause) and the LABEL says the state in words. Those two
 * now do the work the fill used to. If the labels are ever hidden — a
 * narrow viewport dropping the `sm:inline` text, say — the colour alone will
 * NOT carry the distinction, and the alpha must come back up. That is the
 * condition to re-check, not a matter of taste.
 */
const CONNECTED = "bg-emerald-600/50 text-white";
/** Transitional / recovering — work is happening, nothing is lost yet. */
/** amber-500 measured 2.13 against white — unreadable, exactly as the
 *  operator predicted. amber-700 (5.03) is the "dark yellow like gold" that
 *  actually works. */
const BUSY = "bg-amber-700/50 text-white";
/** Hard down — the runtime is gone or unreachable. */
/** 4.77 — clears AA. */
const DOWN = "bg-red-600/50 text-white";
/** Deliberately paused: not an error, so it must not wear an error's colour. */
/** slate-500 was 3.96; slate-600 (7.63) reads cleanly and still says
 *  "deliberately idle" rather than "broken". */
const PAUSED = "bg-slate-600/50 text-white";

export function resolveGatewayDisplay(
  connectionState: string,
  runtimeState: RuntimeState | null,
  repairInProgress: boolean,
): GatewayDisplay {
  if (connectionState === "connected") {
    return {
      Icon: Wifi,
      colour: CONNECTED,
      labelKey: "header.gateway.connected",
      msgKey: "header.gateway.runtime.msg.connected",
    };
  }
  if (repairInProgress) {
    return {
      Icon: Wrench,
      colour: BUSY,
      labelKey: "header.gateway.runtime.repairing",
      msgKey: "header.gateway.runtime.msg.repairing",
    };
  }
  switch (runtimeState) {
    case "provisioning":
    case "restarting":
      return {
        Icon: Loader2,
        colour: BUSY,
        spin: true,
        labelKey: "header.gateway.runtime.starting",
        msgKey: "header.gateway.runtime.msg.starting",
      };
    case "unhealthy":
      return {
        Icon: AlertTriangle,
        colour: BUSY,
        labelKey: "header.gateway.runtime.recovering",
        msgKey: "header.gateway.runtime.msg.recovering",
      };
    case "exited":
      return {
        Icon: Power,
        colour: DOWN,
        labelKey: "header.gateway.runtime.offline",
        msgKey: "header.gateway.runtime.msg.offline",
      };
    case "missing":
      return {
        Icon: Unplug,
        colour: DOWN,
        labelKey: "header.gateway.runtime.unavailable",
        msgKey: "header.gateway.runtime.msg.unavailable",
      };
    case "stopped":
      return {
        Icon: Pause,
        colour: PAUSED,
        labelKey: "header.gateway.runtime.paused",
        msgKey: "header.gateway.runtime.msg.paused",
      };
    default: {
      // healthy / running / orphan / null → a WS-level blip on an otherwise-fine
      // runtime: reconnect. Hard drops (disconnected/error) show WifiOff.
      const hardDrop = connectionState === "disconnected" || connectionState === "error";
      return {
        Icon: hardDrop ? WifiOff : Loader2,
        colour: BUSY,
        spin: !hardDrop,
        labelKey: GATEWAY_KEY_BY_STATE[connectionState] ?? "header.gateway.reconnecting",
        // Match the message to the actual WS state (reuses the existing
        // full-sentence errors.gateway.* keys) so the popover never says
        // "Reconnecting…" while the label says "Connecting…".
        msgKey: `errors.gateway.${connectionState}`,
      };
    }
  }
}

